// ./src/hooks/useExpenseSummaryReport.ts
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import {
  ExpenseSummaryReport,
  ExpenseSummaryReportService,
  ExpenseSummaryReportFilter,
} from "@/utils/api";
import { toApiDateRange } from "@/utils";

// Extend dayjs with UTC plugin
dayjs.extend(utc);

// ============================================
// Types
// ============================================

export interface UseExpenseSummaryReportOptions {
  /** Auto-load data on mount with default filters */
  autoLoad?: boolean;

  /** Current user ID - data reloads when this changes */
  userId?: string | null;

  /** Initial car ID filter */
  carId?: string | null;

  /** Initial tag IDs filter */
  tagIds?: string[];

  /** Initial date from (local date string YYYY-MM-DD) */
  dateFrom?: string | null;

  /** Initial date to (local date string YYYY-MM-DD) */
  dateTo?: string | null;
}

export interface ReportFilters {
  carId: string | null;
  tagIds: string[];
  dateFrom: string | null;
  dateTo: string | null;
}

export interface UseExpenseSummaryReportReturn {
  // Data state
  /** The report data */
  report: ExpenseSummaryReport | null;

  /** Whether the report is loading */
  loading: boolean;

  /** Whether the report has no data */
  isEmpty: boolean;

  // Current filters (local dates)
  /** Current filter state */
  filters: ReportFilters;

  // Core operations
  /** Fetch report with filters (dates should be local YYYY-MM-DD strings) */
  fetchReport: (
    filter?: Partial<ReportFilters>
  ) => Promise<ExpenseSummaryReport | null>;

  /** Refresh report with current filters */
  refresh: () => Promise<void>;

  /** Reset to initial state */
  reset: () => void;

  // Filter setters
  /** Set car ID filter */
  setCarId: (carId: string | null) => void;

  /** Set tag IDs filter */
  setTagIds: (tagIds: string[]) => void;

  /** Set date range filter (local dates) */
  setDateRange: (dateFrom: string | null, dateTo: string | null) => void;

  // Error handling
  /** Last error message */
  lastError: string | null;

  /** Clear the error */
  clearError: () => void;

  // Helpers
  /** Get default date range (last 30 days) */
  getDefaultDateRange: () => { dateFrom: string; dateTo: string };
}

// ============================================
// Constants
// ============================================

const DEFAULT_DAYS_BACK = 30;

// ============================================
// Helper Functions
// ============================================

/**
 * Get default date range (last 30 days) as local date strings
 */
const getDefaultDateRangeStatic = (): { dateFrom: string; dateTo: string } => {
  const today = dayjs().startOf("day");
  const thirtyDaysAgo = today.subtract(DEFAULT_DAYS_BACK - 1, "day");

  return {
    dateFrom: thirtyDaysAgo.format("YYYY-MM-DD"),
    dateTo: today.format("YYYY-MM-DD"),
  };
};

// ============================================
// Hook
// ============================================

export const useExpenseSummaryReport = (
  options: UseExpenseSummaryReportOptions = {}
): UseExpenseSummaryReportReturn => {
  const {
    autoLoad = false,
    userId = null,
    carId: initialCarId = null,
    tagIds: initialTagIds = [],
    dateFrom: initialDateFrom = null,
    dateTo: initialDateTo = null,
  } = options;

  // Track previous user ID for change detection
  const previousUserIdRef = useRef<string | null | undefined>(undefined);

  // Get default dates if not provided
  const defaultDates = useMemo(() => getDefaultDateRangeStatic(), []);

  // Data state
  const [report, setReport] = useState<ExpenseSummaryReport | null>(null);
  const [loading, setLoading] = useState(false);

  // Filter state (local dates)
  const [filters, setFilters] = useState<ReportFilters>({
    carId: initialCarId,
    tagIds: initialTagIds,
    dateFrom: initialDateFrom ?? defaultDates.dateFrom,
    dateTo: initialDateTo ?? defaultDates.dateTo,
  });

  // Use ref to track current filters for stable callbacks
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  // Error state
  const [lastError, setLastError] = useState<string | null>(null);

  // Computed values
  const isEmpty = useMemo(() => report === null && !loading, [report, loading]);

  // ============================================
  // Core Operations
  // ============================================

  /**
   * Fetch report with filters
   * Dates should be provided as local YYYY-MM-DD strings
   */
  const fetchReport = useCallback(
    async (
      filter?: Partial<ReportFilters>
    ): Promise<ExpenseSummaryReport | null> => {
      setLoading(true);
      setLastError(null);

      // Use ref to get current filters (avoids dependency on filters state)
      const currentFilters = filtersRef.current;

      // Merge with current filters
      const mergedFilters: ReportFilters = {
        carId:
          filter?.carId !== undefined ? filter.carId : currentFilters.carId,
        tagIds:
          filter?.tagIds !== undefined ? filter.tagIds : currentFilters.tagIds,
        dateFrom:
          filter?.dateFrom !== undefined
            ? filter.dateFrom
            : currentFilters.dateFrom,
        dateTo:
          filter?.dateTo !== undefined ? filter.dateTo : currentFilters.dateTo,
      };

      // Update filter state only if changed
      const filtersChanged =
        mergedFilters.carId !== currentFilters.carId ||
        mergedFilters.dateFrom !== currentFilters.dateFrom ||
        mergedFilters.dateTo !== currentFilters.dateTo ||
        JSON.stringify(mergedFilters.tagIds) !==
          JSON.stringify(currentFilters.tagIds);

      if (filtersChanged) {
        setFilters(mergedFilters);
      }

      try {
        // Build API filter with UTC dates
        const apiFilter: ExpenseSummaryReportFilter = {};

        if (mergedFilters.carId) {
          apiFilter.carId = [mergedFilters.carId];
        }

        if (mergedFilters.tagIds && mergedFilters.tagIds.length > 0) {
          apiFilter.tagId = mergedFilters.tagIds;
        }

        // Convert local dates to UTC for API
        const { dateFrom, dateTo } = toApiDateRange(
          mergedFilters.dateFrom,
          mergedFilters.dateTo
        );

        if (dateFrom) {
          apiFilter.dateFrom = dateFrom;
        }

        if (dateTo) {
          apiFilter.dateTo = dateTo;
        }

        const result = await ExpenseSummaryReportService.get(apiFilter);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const reportData = Array.isArray(result.data)
            ? (result.data[0] as ExpenseSummaryReport)
            : (result.data as ExpenseSummaryReport);

          setReport(reportData || null);
          return reportData || null;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to load expense summary report";
          setLastError(errorMessage);
          setReport(null);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load expense summary report";
        setLastError(errorMessage);
        setReport(null);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [] // No dependencies - uses refs
  );

  /**
   * Refresh report with current filters
   */
  const refresh = useCallback(async () => {
    await fetchReport();
  }, [fetchReport]);

  /**
   * Reset to initial state
   */
  const reset = useCallback(() => {
    const defaultRange = getDefaultDateRangeStatic();
    setReport(null);
    setFilters({
      carId: initialCarId,
      tagIds: initialTagIds,
      dateFrom: initialDateFrom ?? defaultRange.dateFrom,
      dateTo: initialDateTo ?? defaultRange.dateTo,
    });
    setLastError(null);
    setLoading(false);
  }, [initialCarId, initialTagIds, initialDateFrom, initialDateTo]);

  // ============================================
  // Filter Setters
  // ============================================

  const setCarId = useCallback((carId: string | null) => {
    setFilters((prev) => ({ ...prev, carId }));
  }, []);

  const setTagIds = useCallback((tagIds: string[]) => {
    setFilters((prev) => ({ ...prev, tagIds }));
  }, []);

  const setDateRange = useCallback(
    (dateFrom: string | null, dateTo: string | null) => {
      setFilters((prev) => ({ ...prev, dateFrom, dateTo }));
    },
    []
  );

  // ============================================
  // Error Handling
  // ============================================

  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // ============================================
  // Stable helper function
  // ============================================

  const getDefaultDateRange = useCallback(() => {
    return getDefaultDateRangeStatic();
  }, []);

  // ============================================
  // Auto-load and User Change Effects
  // ============================================

  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = userId;

    // Skip on initial mount (when previousUserId is undefined)
    if (previousUserId === undefined) {
      // Initial load if autoLoad is enabled and user is logged in
      if (autoLoad && userId) {
        fetchReport();
      }
      return;
    }

    // User changed (including sign out)
    if (previousUserId !== userId) {
      // Always reset when user changes
      reset();

      // If new user is logged in and autoLoad is enabled, load their data
      if (autoLoad && userId) {
        fetchReport();
      }
    }
  }, [userId, autoLoad, fetchReport, reset]);

  // ============================================
  // Return
  // ============================================

  return {
    // Data state
    report,
    loading,
    isEmpty,

    // Current filters
    filters,

    // Core operations
    fetchReport,
    refresh,
    reset,

    // Filter setters
    setCarId,
    setTagIds,
    setDateRange,

    // Error handling
    lastError,
    clearError,

    // Helpers
    getDefaultDateRange,
  };
};
